import React, { Fragment, useEffect, useState } from 'react';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import * as R from 'ramda';
import ErrorIcon from '@mui/icons-material/Error';
import CheckboxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import { DrawerLayoutWrapper } from 'common';
import { useTheme } from '@mui/material/styles';
import TableToolTip from '../tables/TableToolTip';

// Utils
import { groups, roleKeys, userAffiliateData } from '../const';

// Styles
import {
  affiliateListItemStyles,
  affiliateListStyleStyles,
  affiliateListToolTipStyles,
  affilliatePermissionListItemCheckBoxStyles,
  affilliatePermissionListItemLabelStyles,
  affilliatePermissionListItemStyles,
  drawerStyles,
  featurePermissionsListHeaderStyles,
  featurePermissionsListItemDividerStyles,
  featurePermissionsListItemStyles,
  featurePermissionsStyles,
  keyStyles,
} from './UserEditDrawer.styles';

const getAffiliatePermissions = user => {
  return user?.affiliates?.reduce((a, c) => {
    return { ...a, [`${c.companyCustomerIdName}-${c.dataCompanyId}`]: c.hasAccess };
  }, {});
};

const getGroupPermissions = user => {
  return groups?.reduce((a, c) => {
    return { ...a, [c]: user?.groups?.includes(c) || false };
  }, {});
};

const UserEditDrawer = ({
  affiliates,
  customerId,
  drawerOpen,
  setDrawerOpen,
  user,
  targetUserPermissionsPending,
  setTargetUser,
  updateUserPermission,
}) => {
  const theme = useTheme();
  const showDispatching = process.env.REACT_APP_SHOW_DISPATCHING === 'true';
  const [affiliatePermissions, setAffiliatePermissions] = useState([]);
  const [groupPermissions, setGroupPermissions] = useState([]);
  const [initialState, setInitialState] = useState({
    affiliatePermissions: [],
    groupPermissions: [],
  });
  const [changed, setChanged] = useState(false);
  const [mergedData, setMergedData] = useState([]);

  useEffect(() => {
    setInitialState({
      affiliatePermissions: getAffiliatePermissions(user),
      groupPermissions: getGroupPermissions(user?.roles),
    });
    setAffiliatePermissions(getAffiliatePermissions(user));
    setGroupPermissions(getGroupPermissions(user?.roles));
    setChanged(false);
  }, [user]);

  useEffect(() => {
    if (R.not(R.isNil(affiliatePermissions)) && R.not(R.isNil(groupPermissions))) {
      setChanged(
        R.or(
          R.not(R.equals(affiliatePermissions, initialState.affiliatePermissions)),
          R.not(R.equals(groupPermissions, initialState.groupPermissions))
        )
      );
    }
  }, [affiliatePermissions, groupPermissions, initialState]);

  // Data to create the table: Now if the ENV is production, we filter out the columns we do not want
  // the user to see, if using filteredData Dispatching is filtered out  - otherwise use the full userAffiliateData
  const filteredData = userAffiliateData.filter(({ type }) => type !== 'Dispatching');
  const dataBasedOnEnvironment = !showDispatching ? filteredData : userAffiliateData;

  const handleClose = () => {
    setTargetUser(null);
    setDrawerOpen(false);
  };
  const handleSave = () => {
    const updatedUser = {
      affiliates: [],
      customerId,
      requestorUserId: user.userId,
      roles: [],
      userId: user.userId,
    };
    updatedUser.affiliates = Object.keys(affiliatePermissions).reduce((a, c) => {
      const ids = c.split('-');
      const current = user.affiliates.find(
        affiliate =>
          affiliate.companyCustomerIdName === ids[0] && affiliate.dataCompanyId === parseInt(ids[1])
      );
      return [
        ...a,
        {
          ...current,
          companyCustomerIdName: ids[0],
          dataCompanyId: ids[1],
          hasAccess: affiliatePermissions[c],
        },
      ];
    }, []);
    updatedUser.roles = Object.keys(groupPermissions).filter(k => groupPermissions[k]);
    updateUserPermission(updatedUser);
    setTargetUser(null);
    setDrawerOpen(false);
  };

  useEffect(() => {
    if (R.not(R.isNil(user)) && R.not(R.isNil(user?.roles))) {
      const checkRoles = (roles, permissions) => {
        let updatedPermissions = R.clone(permissions);
        R.forEach(role => {
          if (R.has(role, permissions)) {
            // eslint-disable-next-line no-param-reassign
            updatedPermissions = R.assoc(role, true, updatedPermissions);
          }
        }, roles);
        return updatedPermissions;
      };
      const updatedRolePermissions = checkRoles(user?.roles, groupPermissions);

      setGroupPermissions(updatedRolePermissions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (R.not(R.isNil(user)) && R.not(R.isNil(user?.affiliates))) {
      const affiliateMap = R.indexBy(R.prop('companyCustomerIdName'), user?.affiliates);

      const mergedUserAffiliateData = R.map(affiliate => {
        const key = affiliate.companyCustomerIdName;
        const x = affiliateMap[key] || {};
        return R.mergeRight(affiliate, x);
      }, affiliates);
      setMergedData(mergedUserAffiliateData);
    }
  }, [user, affiliates]);

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
      <Button
        disabled={targetUserPermissionsPending}
        onClick={handleClose}
        sx={{ width: 150 }}
        variant="outlined"
      >
        Close
      </Button>
      <Button
        disabled={R.not(changed) || targetUserPermissionsPending}
        onClick={handleSave}
        sx={{ width: 150 }}
      >
        Save
      </Button>
    </Box>
  );
  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      slotProps={{
        paper: { sx: drawerStyles(theme) },
      }}
    >
      <DrawerLayoutWrapper
        buttons={buttons}
        headerButtonText="CLOSE"
        isLoading={targetUserPermissionsPending}
        onHeaderButtonClick={handleClose}
        title="Edit User"
      >
        {user && (
          <Stack sx={{ pt: '1rem', px: '2rem' }}>
            <Box sx={{ mt: '10px' }}>
              {roleKeys.map(({ name, acronym, color }) => (
                <Box key={name} sx={{ ...keyStyles(theme), display: 'inline-grid', mb: 1, mr: 1 }}>
                  <Chip
                    label={acronym}
                    sx={{
                      '& .MuiChip-label': {
                        marginTop: '1px',
                        padding: 0,
                      },
                      alignItems: 'center',
                      bgcolor: color,
                      borderRadius: '4px',
                      display: 'flex',
                      height: 20,
                      justifyContent: 'center',
                      padding: '2px',
                      width: 30,
                    }}
                  />
                  <span>{name}</span>
                </Box>
              ))}
            </Box>
            <Divider sx={{ mb: 3, mt: 1 }} />
            {user.error ? (
              <Box style={{ height: '100%', textAlign: 'center', width: '100%' }}>
                <ErrorIcon color="error" sx={{ height: 48, width: 48 }} />
                <Typography color="text.primary" variant="h6">
                  Error
                </Typography>
                <Typography variant="body1">{user.error}</Typography>
              </Box>
            ) : (
              <>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 400,
                    textAlign: 'left',
                  }}
                  variant="body1"
                >
                  User Id
                </Typography>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    marginBottom: '5px',
                    textAlign: 'left',
                  }}
                  variant="h6"
                >
                  {user.userId}
                </Typography>

                {user.firstName && user.lastName && (
                  <>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 400,
                        textAlign: 'left',
                      }}
                      variant="body1"
                    >
                      Name
                    </Typography>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        marginBottom: '5px',
                        textAlign: 'left',
                      }}
                      variant="h6"
                    >
                      {user.firstName} {user.lastName}
                    </Typography>
                  </>
                )}

                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 400,
                    textAlign: 'left',
                  }}
                  variant="body1"
                >
                  Username (Email)
                </Typography>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    marginBottom: '5px',
                    textAlign: 'left',
                  }}
                  variant="h6"
                >
                  {user.userName}
                </Typography>

                <Divider sx={{ mb: 3, mt: 1 }} />
                <Typography
                  sx={{
                    ...featurePermissionsListHeaderStyles,
                    color: 'text.secondary',
                    textAlign: 'left',
                  }}
                  variant="h6"
                >
                  Affiliate IDs
                </Typography>
                {R.not(R.isEmpty(affiliates)) &&
                  R.not(R.isNil(affiliatePermissions)) &&
                  mergedData?.map(
                    ({
                      dataCompany,
                      dataCompanyId,
                      inventoryOwner,
                      nameShort,
                      companyCustomerIdName,
                      refiner,
                      shipper,
                      supplier,
                      carrier,
                    }) => (
                      <div key={`${companyCustomerIdName}-${dataCompanyId}-${dataCompany}`}>
                        <Box sx={{ ...affiliateListStyleStyles }}>
                          <Box sx={{ ...affilliatePermissionListItemStyles }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    affiliatePermissions[
                                      `${companyCustomerIdName}-${dataCompanyId}`
                                    ]
                                  }
                                  checkedIcon={
                                    <CheckboxRoundedIcon
                                      sx={{
                                        ...affilliatePermissionListItemCheckBoxStyles,
                                        color: 'secondary.main',
                                      }}
                                    />
                                  }
                                  sx={{
                                    '&.MuiCheckbox-root': {
                                      borderRadius: '50%',
                                      color: 'text.secondary',
                                    },
                                  }}
                                />
                              }
                              label=""
                              onChange={() => {
                                setAffiliatePermissions(state => {
                                  return {
                                    ...state,
                                    [`${companyCustomerIdName}-${dataCompanyId}`]:
                                      !state[`${companyCustomerIdName}-${dataCompanyId}`],
                                  };
                                });
                              }}
                            />
                            <Box sx={{ ...affilliatePermissionListItemLabelStyles }}>
                              <Box sx={{ ...affiliateListItemStyles }}>
                                <Typography noWrap variant="body1">
                                  {companyCustomerIdName} {nameShort}
                                </Typography>
                              </Box>
                              <Box sx={{ ...affiliateListItemStyles }}>
                                <Typography
                                  noWrap
                                  sx={{ ...affiliateListToolTipStyles }}
                                  variant="body1"
                                >
                                  {dataCompany}
                                </Typography>
                                {inventoryOwner && (
                                  <TableToolTip
                                    acronym="IO"
                                    color="#9FA8DA"
                                    name="Inventory Owner"
                                  />
                                )}
                                {/* This teal color below isn't in our v3 lib */}
                                {refiner && (
                                  <TableToolTip acronym="RF" color="#80CBC4" name="Refiner" />
                                )}
                                {/* yellow here not in theme but IS in v3 lib */}
                                {shipper && (
                                  <TableToolTip acronym="SH" color="#FFE082" name="Shipper" />
                                )}
                                {/* blue here is NOT in theme but IS in v3 */}
                                {supplier && (
                                  <TableToolTip acronym="SP" color="#90CAF9" name="Supplier" />
                                )}
                                {carrier && (
                                  <TableToolTip acronym="CR" color="#FFCDD2" name="Carrier" />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        <Divider sx={{ mb: 1, mt: 1 }} />
                      </div>
                    )
                  )}

                <Box sx={{ ...featurePermissionsStyles }}>
                  <Box sx={{ ...featurePermissionsListHeaderStyles }}>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        textAlign: 'left',
                      }}
                      variant="h6"
                    >
                      Feature Permissions
                    </Typography>
                  </Box>
                  <Box sx={{ ...featurePermissionsListHeaderStyles }}>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        textAlign: 'left',
                      }}
                      variant="body1bold"
                    >
                      View
                    </Typography>
                  </Box>
                  <Box sx={{ ...featurePermissionsListHeaderStyles }}>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        textAlign: 'left',
                      }}
                      variant="body1bold"
                    >
                      Manage
                    </Typography>
                  </Box>

                  {dataBasedOnEnvironment.map(({ type, affiliateType, groupName }) => (
                    <Fragment key={type}>
                      <Box sx={{ ...featurePermissionsListItemStyles }}>
                        <Typography noWrap sx={{ mb: 1 }} variant="body1">
                          {type} <br />
                          <small>{groupName}</small>
                        </Typography>
                        <Box key={type} sx={{ ...affiliateListItemStyles }}>
                          {affiliateType.map(({ acronym, color, name }) => (
                            <TableToolTip acronym={acronym} color={color} key={name} name={name} />
                          ))}
                        </Box>
                      </Box>
                      <Box sx={{ ...featurePermissionsListItemStyles }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={groupPermissions[`${groupName}: View`]}
                              checkedIcon={
                                <CheckboxRoundedIcon
                                  sx={{
                                    ...affilliatePermissionListItemCheckBoxStyles,
                                    color: 'secondary.main',
                                  }}
                                />
                              }
                              disabled={groupPermissions[`${groupName}: Manage`]}
                              sx={{
                                '&.Mui-disabled': {
                                  '& .MuiSvgIcon-root': {
                                    color: 'text.disabled',
                                  },
                                  opacity: 1,
                                },
                                '&.MuiCheckbox-root': {
                                  borderRadius: '50%',
                                  color: 'text.secondary',
                                },
                              }}
                            />
                          }
                          label=""
                          onChange={() => {
                            setGroupPermissions(state => {
                              return {
                                ...state,
                                [`${groupName}: View`]: !state[`${groupName}: View`],
                              };
                            });
                          }}
                        />
                      </Box>
                      <Box sx={{ ...featurePermissionsListItemStyles }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={groupPermissions[`${groupName}: Manage`]}
                              checkedIcon={
                                <CheckboxRoundedIcon
                                  sx={{
                                    ...affilliatePermissionListItemCheckBoxStyles,
                                    color: 'secondary.main',
                                  }}
                                />
                              }
                              sx={{
                                '&.MuiCheckbox-root': {
                                  borderRadius: '50%',
                                  color: 'text.secondary',
                                },
                              }}
                            />
                          }
                          label=""
                          onChange={() => {
                            setGroupPermissions(state => {
                              const isManageChecked = !state[`${groupName}: Manage`];
                              return {
                                ...state,
                                [`${groupName}: Manage`]: isManageChecked,
                                // If Manage is being checked, ensure View is also checked
                                [`${groupName}: View`]: isManageChecked
                                  ? true
                                  : state[`${groupName}: View`],
                              };
                            });
                          }}
                        />
                      </Box>
                      <Divider sx={{ ...featurePermissionsListItemDividerStyles, mb: 1, mt: 1 }} />
                    </Fragment>
                  ))}
                </Box>
              </>
            )}
          </Stack>
        )}
      </DrawerLayoutWrapper>
    </Drawer>
  );
};

UserEditDrawer.propTypes = {
  affiliates: arrayOf(shape({})).isRequired,
  customerId: number.isRequired,
  drawerOpen: bool.isRequired,
  setDrawerOpen: func.isRequired,
  setTargetUser: func.isRequired,
  targetUserPermissionsPending: bool,
  updateUserPermission: func.isRequired,
  user: shape({}),
};

UserEditDrawer.defaultProps = {
  targetUserPermissionsPending: true,
  user: null,
};

export default UserEditDrawer;
